import type { Chef } from '../types';
import type { FacebookOwner, InstagramOwner } from '../services/geminiService';
import {
    findChefByFacebookPageSlug,
    findChefByInstagramUsername,
    findChefByUrls,
    getChefWebsites,
} from './chefDisplay';
import { resolveFacebookChef } from './resolveFacebookChef';
import { resolveInstagramChef } from './resolveInstagramChef';

type ParsedRecipeChefSource = {
    instagramUsername?: string;
    instagramOwner?: InstagramOwner;
    facebookOwner?: FacebookOwner;
    links?: string[];
};

export async function creditChefFromParsedRecipe(
    result: ParsedRecipeChefSource,
    existingChefs: Chef[],
    options: {
        userId?: string;
        onResolveInstagramChef?: (owner: InstagramOwner) => Promise<Chef | null>;
        extraUrls?: string[];
    } = {},
): Promise<{ chef: Chef | null; createdFromSource: boolean }> {
    const { userId, onResolveInstagramChef, extraUrls = [] } = options;
    let createdFromSource = false;

    const ownerUsername = result.instagramOwner?.username || result.instagramUsername;
    if (ownerUsername) {
        try {
            const resolveChef = onResolveInstagramChef
                ?? ((owner: InstagramOwner) => resolveInstagramChef(owner, existingChefs));
            const hadChef = existingChefs.some(
                (chef) => chef.englishName.toLowerCase() === ownerUsername.toLowerCase()
                    || getChefWebsites(chef).some((link) => link.includes(`instagram.com/${ownerUsername}`)),
            );
            const resolvedChef = await resolveChef({
                username: ownerUsername,
                fullName: result.instagramOwner?.fullName,
                profilePicUrl: result.instagramOwner?.profilePicUrl,
                userId: result.instagramOwner?.userId,
            });
            if (resolvedChef) {
                return { chef: resolvedChef, createdFromSource: !hadChef };
            }
        } catch (error) {
            console.error('Instagram chef auto-create failed:', error);
        }
    }

    if (result.facebookOwner?.pageSlug) {
        try {
            const hadChef = findChefByFacebookPageSlug(
                existingChefs,
                result.facebookOwner.pageSlug,
                userId,
            ) != null;
            const resolvedChef = await resolveFacebookChef(result.facebookOwner, existingChefs);
            if (resolvedChef) {
                return { chef: resolvedChef, createdFromSource: !hadChef };
            }
        } catch (error) {
            console.error('Facebook chef auto-create failed:', error);
        }
    }

    const urlsToMatch = [...extraUrls, ...(result.links ?? [])];
    const matchedChef = (result.instagramUsername
        ? findChefByInstagramUsername(existingChefs, result.instagramUsername, userId)
        : null)
        ?? (result.facebookOwner?.pageSlug
            ? findChefByFacebookPageSlug(existingChefs, result.facebookOwner.pageSlug, userId)
            : null)
        ?? findChefByUrls(existingChefs, urlsToMatch, userId);

    return { chef: matchedChef, createdFromSource: false };
}
